import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Style/Login.css';
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaClock,       // ← NEW: icon for Clock In / Out
} from 'react-icons/fa';
import { auth, db } from '../firebaseConfig';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import {
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CompanyLogo from '../assets/CompanyLogo.png';

export default function Login() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading]         = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember]       = useState(true);
  const [capsOn, setCapsOn]           = useState(false);
  const [shake, setShake]             = useState(false);

  const statusRef = useRef(null);
  const pwdRef    = useRef(null);

  /* Focus email on mount */
  useEffect(() => {
    document.getElementById('email')?.focus();
  }, []);

  /* Caps lock detector */
  const handleKeyState = e => {
    if (e.getModifierState) {
      setCapsOn(e.getModifierState('CapsLock'));
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyState);
    window.addEventListener('keyup', handleKeyState);
    return () => {
      window.removeEventListener('keydown', handleKeyState);
      window.removeEventListener('keyup', handleKeyState);
    };
  }, []);

  /* Shake reset */
  useEffect(() => {
    if (shake) {
      const t = setTimeout(() => setShake(false), 600);
      return () => clearTimeout(t);
    }
  }, [shake]);

  const handleChange = e =>
    setCredentials(prev => ({ ...prev, [e.target.id]: e.target.value }));

  const persistMode = remember ? browserLocalPersistence : browserSessionPersistence;

  const handleSubmit = async e => {
    e.preventDefault();
    const { email, password } = credentials;

    if (!email.trim() || !password) {
      toast.error('Enter both email and password.');
      setShake(true);
      return;
    }

    setLoading(true);
    try {
      await setPersistence(auth, persistMode);

      const cred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      toast.success('Logged in successfully!');
      statusRef.current && (statusRef.current.textContent = 'Login successful.');

      /* Audit */
      const { user } = cred;
      await addDoc(collection(db, 'Audit'), {
        uid: user.uid,
        email: user.email,
        event: 'login',
        userAgent: navigator.userAgent,
        tzOffset: new Date().getTimezoneOffset(),
        remember,
        createdAt: serverTimestamp(),
      });

      navigate('/Jobs');
    } catch (err) {
      console.error(err);
      statusRef.current && (statusRef.current.textContent = 'Login failed.');
      let msg = 'Login failed. Please try again.';
      switch (err.code) {
        case 'auth/user-not-found':   msg = 'No account found with that email.'; break;
        case 'auth/wrong-password':   msg = 'Incorrect password. Try again.'; break;
        case 'auth/invalid-email':    msg = 'That email address is invalid.'; break;
        case 'auth/too-many-requests':msg = 'Too many attempts. Please wait and retry.'; break;
        default: break;
      }
      toast.error(msg);
      setShake(true);
      pwdRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = credentials.email.trim();
    if (!email) {
      toast.error('Enter your email to reset password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent.');
    } catch (err) {
      console.error(err);
      toast.error('Could not send reset email.');
    }
  };

  return (
    <div className="login-container">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className={`card-wrapper ${shake ? 'shake' : ''}`}>
        {/* Brand / Info Panel */}
        <aside className="art-board" aria-label="Company branding">
          <div className="logo-plate">
            <img
              src={CompanyLogo}
              alt="Company Logo"
              className="company-logo"
            />
            <div className="company-block">
              <h1 className="co-name">Nyabanga Engineering</h1>
              <p className="co-line">Plot 35, Pretoria Rd, Benoni</p>
              <p className="co-line">011 420-1537 | 082 471-9512</p>
              <p className="co-line">
                <a href="mailto:info@nyabanga.co.za">info@nyabanga.co.za</a>
              </p>
            </div>
          </div>

        

          <div className="bg-accent one" />
          <div className="bg-accent two" />
        </aside>

        {/* Form Panel */}
        <section className="form-panel" aria-labelledby="login-heading">
          <h2 id="login-heading">Welcome Back</h2>
          <p className="subtitle">Sign in to continue</p>

          {/* Quick action for anyone to clock in/out (duplicate here for visibility) */}
          <div style={{ margin: '0.5rem 0 1rem', display: 'flex', justifyContent: 'flex-start' }}>
            <button
              type="button"
              onClick={() => navigate('/ClockIn')}
              className="btn-clockin"
              title="Go to Clock In / Out"
              style={{
                background: '#00cfff',       /* aqua */
                color: '#ffffff',            /* white text */
                border: '2px solid #00cfff',
                borderRadius: '10px',
                padding: '0.55rem 0.9rem',
                fontWeight: 400,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,.10)',
                cursor: 'pointer'
              }}
            >
              <FaClock aria-hidden="true" />
              Clock In / Out
            </button>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <label htmlFor="email">Email</label>
            <div className="input-row">
              <FaUser className="icon" aria-hidden="true" />
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={credentials.email}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="username"
              />
            </div>

            {/* Password */}
            <label htmlFor="password" className="pw-label-row">
              <span>Password</span>
              {capsOn && (
                <span className="caps-indicator" role="alert">
                  CAPS LOCK ON
                </span>
              )}
            </label>
            <div className="input-row">
              <FaLock className="icon" aria-hidden="true" />
              <input
                id="password"
                ref={pwdRef}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={credentials.password}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="current-password"
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSubmit(e);
                }}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(p => !p)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Remember + Forgot */}
            <div className="form-meta-row">
              <label className="remember-wrap">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <button
                type="button"
                className="link-btn small"
                onClick={handleForgotPassword}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
              aria-busy={loading}
            >
              {loading && <span className="spinner" aria-hidden="true" />}
              {loading ? 'Logging in…' : 'Login'}
            </button>
          </form>

          <div className="helper-links">
            <button
              type="button"
              className="link-btn"
              onClick={() => navigate('/register')}
            >
              Register
            </button>
          </div>

          <div
            ref={statusRef}
            aria-live="polite"
            className="sr-status"
          />
        </section>
      </div>
    </div>
  );
}
